<template>
  <div v-if="!productFetching && !product"></div>
  <div v-else-if="productFetching" class="grid-spans-2 grid w-auto gap-1">
    <div class="grid-spans-3 grid w-auto gap-16 sm:hidden">
      <Skeleton
        type="custom"
        class="max-h-md group aspect-h-5 aspect-w-10 relative flex items-center justify-center bg-gray"
      />
      <Skeleton
        type="custom"
        class="max-h-md group aspect-h-2 aspect-w-10 relative flex items-center justify-center bg-gray"
      />
      <Skeleton
        type="custom"
        class="max-h-md group aspect-h-5 aspect-w-10 relative flex items-center justify-center bg-gray"
      />
    </div>
    <div class="hidden w-auto grid-cols-3 gap-1 sm:grid">
      <Skeleton
        type="custom"
        class="max-h-md group aspect-h-14 aspect-w-10 relative flex items-center justify-center bg-gray"
      />
      <Skeleton
        type="custom"
        class="max-h-md group aspect-h-14 aspect-w-10 relative flex items-center justify-center bg-gray"
      />
      <div class="grid-spans-3 grid w-auto gap-20">
        <Skeleton
          type="custom"
          class="max-h-md group aspect-h-1 aspect-w-10 relative mx-16 my-10 flex items-center justify-center bg-gray"
        />
        <div class="grid w-auto grid-cols-2 gap-1">
          <Skeleton
            type="custom"
            class="max-h-md group aspect-h-4 aspect-w-8 relative ml-16 flex items-center justify-center bg-gray"
          />
        </div>

        <Skeleton
          type="custom"
          class="max-h-md group aspect-h-1 aspect-w-10 relative mx-10 mb-16 mt-36 flex items-center justify-center bg-gray"
        />
      </div>
    </div>
    <div class="hidden w-auto grid-cols-3 gap-1 sm:grid">
      <Skeleton
        type="custom"
        class="max-h-md group aspect-h-14 aspect-w-10 relative flex items-center justify-center bg-gray"
      />
      <Skeleton
        type="custom"
        class="max-h-md group aspect-h-14 aspect-w-10 relative flex items-center justify-center bg-gray"
      />
    </div>
  </div>
  <PageContent
    v-else-if="currentProduct"
    data-test-id="product-detail-page"
    :data-siblings-prefetched="familyProducts.length > 0 ? 'true' : undefined"
    class="!mt-0"
  >
    <ClothingPdp
      :product="currentProduct"
      :highlighted-order-number="orderNumber"
      :enable-seo="true"
    >
      <template #pre-content="ctx">
        <BreadcrumbsMobile
          v-if="isMobile"
          is-pdp
          :links="ctx.breadcrumbs"
          class="!my-1"
        />
        <Breadcrumbs v-else :links="ctx.breadcrumbs" class="!my-1" />
      </template>

      <template #details-header="ctx">
        <PdpDetailsHeader v-bind="ctx" />
      </template>

      <template #product-price="ctx">
        <PdpPrice v-bind="ctx" />
      </template>

      <template #color-picker="ctx">
        <PdpColorPicker
          :product="ctx.product"
          :highlighted-order-number="ctx.highlightedOrderNumber"
        />
      </template>

      <template #visuals="ctx">
        <PdpVisuals v-bind="ctx" />
      </template>

      <template #product-benefits="ctx">
        <ProductBenefitsNew :product="ctx.product" />
      </template>

      <template #cta="ctx">
        <PdpCta v-bind="ctx" tracking-type="pdp" />
        <PdpDelivery v-bind="ctx" />
      </template>

      <template #motivation-layer="ctx">
        <ProductMotivationLayer :product="ctx.product" />
        <PdpTrustBenefits />
      </template>

      <template #size-picker="ctx">
        <PdpSizePicker v-bind="ctx" :enable-sizekick="true" />
      </template>

      <template #cta-fab="ctx">
        <PdpCtaFab v-bind="ctx" tracking-type="pdp" />
      </template>

      <template v-if="!isMobile" #product-details="ctx">
        <ProductDetailsAccordion :product="ctx.product" variant="plain" />
      </template>

      <template #post-content="ctx">
        <PdpDescription :product="ctx.product" />
      </template>
    </ClothingPdp>
  </PageContent>
</template>

<script setup lang="ts">
import { useDeviceType } from '~/composables/useDeviceType'
import PdpColorPicker from '#layers/base/components/product/pdps/sections/pdp/PdpColorPicker.vue'
import PdpVisuals from '~/components/product/pdps/components/PdpVisuals.vue'
import BreadcrumbsMobile from '#layers/base/components/layout/navigations/BreadcrumbsMobile.vue'
import Breadcrumbs from '~/components/layout/navigations/Breadcrumbs.vue'
import PdpSizePicker from '#layers/base/components/product/pdps/sections/common/PdpSizePicker.vue'
import PdpCtaFab from '#layers/base/components/product/pdps/sections/pdp/PdpCtaFab.vue'
import PdpDelivery from '#layers/base/components/product/pdps/sections/common/PdpDelivery.vue'
import PdpDescription from '~/components/product/pdps/sections/pdp/PdpDescription.vue'
import ProductDetailsAccordion from '~/components/product/productDetailsContent/ProductDetailsAccordion.vue'
import PdpCta from '~/components/product/pdps/sections/pdp/PdpCta.vue'
import PdpTrustBenefits from '~/components/product/pdps/sections/common/PdpTrustBenefits.vue'
import { useLastSeenProducts } from '#layers/base/composables/useLastSeenProducts'

const route = useRoute()

const { isMobile } = useDeviceType()
const productId = computed(() => Number(route.params.id))
const orderNumber = computed(() =>
  (route.query.ordernumber || route.query.on || '').toString(),
)
const { addProductIdToLastSeenCookie } = useLastSeenProducts()

useRouteCache((helper) => {
  helper.setMaxAge(3600).setCacheable()
})

const {
  data: product,
  refresh: fetchProduct,
  error,
  status,
} = useProduct(
  {
    params: computed(() => ({
      id: productId.value,
      with: PDP_PRODUCT_DETAILS_WITH,
      pricePromotionKey: DEFAULT_PRICE_PROMOTION_KEY,
    })),
    options: {
      immediate: false,
      watch: [],
    },
  },
  `useProduct-${productId.value}`,
)

const productFetching = computed(() => status.value === 'pending')

const fetch = async () => {
  await fetchProduct()

  if (error.value) {
    throw createError({
      statusCode: error.value.statusCode,
      message: error.value.message,
    })
  }
}

// Fast color switching (ECOM-808/ECOM-2626): the initial fetch only carries
// trimmed siblings so the SSR payload stays small. After hydration we load
// the full sibling details once in the background; switching colors then
// resolves from memory instead of refetching and remounting into the
// skeleton.
const { data: productFamily, refresh: fetchProductFamily } = useProduct(
  {
    params: computed(() => ({
      id: product.value?.id ?? productId.value,
      with: PRODUCT_SIBLINGS_DETAILS_WITH,
      pricePromotionKey: DEFAULT_PRICE_PROMOTION_KEY,
    })),
    options: {
      immediate: false,
      watch: [],
    },
  },
  `useProductFamily-${productId.value}`,
)

const familyProducts = computed(() => {
  if (!productFamily.value) {
    return []
  }

  const { siblings = [], ...familyProduct } = productFamily.value
  const products = [familyProduct, ...siblings]

  return products.map((item) => ({
    ...item,
    siblings: products.filter((p) => p.id !== item.id),
  }))
})

const prefetchProductFamily = () => {
  if (import.meta.server) {
    return
  }
  if (familyProducts.value.some((item) => item.id === product.value?.id)) {
    return
  }
  // Background enhancement only — on failure color switching just falls
  // back to the fetch-per-switch path below.
  fetchProductFamily().catch(() => {})
}

onNuxtReady(prefetchProductFamily)

watch(() => product.value?.id, prefetchProductFamily)

watch(
  () => route.params.id,
  async (newId) => {
    const newIdAsNumber = Number(newId)
    if (
      product.value?.id !== newIdAsNumber &&
      !familyProducts.value.some((item) => item.id === newIdAsNumber)
    ) {
      await fetch()
    }
  },
)

watch(
  () => product.value,
  async (newVal) => {
    if (newVal && newVal.id) {
      addProductIdToLastSeenCookie(newVal.id)
    }
  },
  { immediate: true },
)

await fetchOnNavigation(async () => {
  await fetch()
})

const productWithSiblings = computed(() => {
  if (!product.value) {
    return []
  }

  const { siblings = [], ...initialProduct } = product.value
  const products = [initialProduct, ...siblings]

  return products.map((item) => ({
    ...item,
    siblings: products.filter((p) => p.id !== item.id),
  }))
})
const currentProduct = computed(() => {
  if (product.value?.id === productId.value) {
    return productWithSiblings.value.find(
      (sibling) => sibling.id === productId.value,
    )
  }

  // Color switch: the route already points at a sibling — serve it from the
  // background-loaded family instead of refetching.
  return familyProducts.value.find((item) => item.id === productId.value)
})

defineOptions({ name: 'ProductDetailPage' })
definePageMeta({
  pageType: 'detail',
  // Ensure only the slug is treated as the key
  // otherwise the product will be fetched multiple times
  // on id changes
  key: (route) => {
    const slug = Array.isArray(route.params.slug)
      ? route.params.slug[0]
      : route.params.slug
    return slug || 'product' // Fallback
  },
})
</script>
